import React, { useState, useEffect } from 'react';
import {
  Camera,
  Mic,
  MapPin,
  Bell,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ArrowRight,
  Sparkles,
  Info,
  ChevronDown,
  ChevronUp,
  Settings,
  Lock,
  Smartphone,
  RotateCw,
} from 'lucide-react';
import {
  requestCameraPermission,
  requestMicrophonePermission,
  requestLocationPermission,
  requestNotificationPermission,
  markPermissionOnboardingCompleted,
  AppPermissionsStatus,
  getStoredPermissions,
  PermissionState,
} from '../../lib/permissions';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { useLanguage } from '../../i18n/LanguageContext';

interface FirstOpenPermissionFlowModalProps {
  isOpen: boolean;
  onComplete: () => void;
  onOpenLogin?: () => void;
}

type FlowStep = 'intro' | 'camera' | 'microphone' | 'location' | 'notification' | 'summary';

export const FirstOpenPermissionFlowModal: React.FC<FirstOpenPermissionFlowModalProps> = ({
  isOpen,
  onComplete,
  onOpenLogin,
}) => {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState<FlowStep>('intro');
  const [permissions, setPermissions] = useState<AppPermissionsStatus>(getStoredPermissions());
  const [isRequesting, setIsRequesting] = useState(false);
  const [showWhyDetails, setShowWhyDetails] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setPermissions(getStoredPermissions());
      setCurrentStep('intro');
      setStepError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Step 1: Request Camera
  const handleRequestCamera = async () => {
    setIsRequesting(true);
    setStepError(null);
    const res = await requestCameraPermission();
    setIsRequesting(false);
    setPermissions(getStoredPermissions());

    if (res.state === 'granted') {
      setShowWhyDetails(false);
      setCurrentStep('microphone');
    } else {
      setStepError(res.error || 'Camera permission was not granted.');
    }
  };

  // Step 2: Request Microphone
  const handleRequestMicrophone = async () => {
    setIsRequesting(true);
    setStepError(null);
    const res = await requestMicrophonePermission();
    setIsRequesting(false);
    setPermissions(getStoredPermissions());

    if (res.state === 'granted') {
      setShowWhyDetails(false);
      setCurrentStep('location');
    } else {
      setStepError(res.error || 'Microphone permission was not granted.');
    }
  };

  // Step 3: Request Location
  const handleRequestLocation = async () => {
    setIsRequesting(true);
    setStepError(null);
    const res = await requestLocationPermission();
    setIsRequesting(false);
    setPermissions(getStoredPermissions());

    if (res.state === 'granted') {
      setShowWhyDetails(false);
      setCurrentStep('notification');
    } else {
      setStepError(res.error || 'Location permission was not granted.');
    }
  };

  // Step 4: Request Notification
  const handleRequestNotification = async () => {
    setIsRequesting(true);
    setStepError(null);
    const res = await requestNotificationPermission();
    setIsRequesting(false);
    setPermissions(getStoredPermissions());

    setShowWhyDetails(false);
    setCurrentStep('summary');
  };

  // Step 5: Finish flow
  const handleFinishOnboarding = (openLogin: boolean = false) => {
    markPermissionOnboardingCompleted();
    onComplete();
    if (openLogin && onOpenLogin) {
      onOpenLogin();
    }
  };

  const getStepProgress = () => {
    switch (currentStep) {
      case 'intro':
        return 0;
      case 'camera':
        return 25;
      case 'microphone':
        return 50;
      case 'location':
        return 75;
      case 'notification':
        return 90;
      case 'summary':
        return 100;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Top Progress Bar */}
        <div className="w-full bg-slate-800 h-1.5 shrink-0">
          <div
            className="h-full bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 transition-all duration-300"
            style={{ width: `${getStepProgress()}%` }}
          />
        </div>

        {/* Modal Header */}
        <div className="px-6 pt-5 pb-3 border-b border-slate-800/80 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white shadow-sm">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <span className="text-[10px] font-mono uppercase tracking-wider text-pink-400 font-bold block">
                PairUpToday Onboarding
              </span>
              <h2 className="text-sm font-black text-white">App Permissions Setup</h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="compact" />
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-800/80 border border-slate-700/60 text-[11px] font-mono text-slate-300">
              <Smartphone className="w-3.5 h-3.5 text-pink-400" />
              <span>Android / iOS</span>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6 overflow-y-auto space-y-5 scrollbar-thin">
          {/* ========================================================= */}
          {/* 1. INTRO SCREEN */}
          {/* ========================================================= */}
          {currentStep === 'intro' && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/30 text-pink-300 text-xs font-bold">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>First App Launch</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                  {t('permissions.title')}
                </h3>
                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed max-w-md mx-auto">
                  {t('permissions.subtitle')}
                </p>
              </div>

              {/* 4 Cards Overview */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="p-3.5 rounded-2xl bg-slate-950/70 border border-slate-800 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-pink-500/15 border border-pink-500/30 flex items-center justify-center text-pink-400 shrink-0 mt-0.5">
                    <Camera className="w-4 h-4" />
                  </div>
                  <div>
                    <strong className="block text-xs font-bold text-white">1. Camera</strong>
                    <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                      Selfie KYC verification & profile photo capture.
                    </p>
                  </div>
                </div>

                <div className="p-3.5 rounded-2xl bg-slate-950/70 border border-slate-800 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center text-purple-400 shrink-0 mt-0.5">
                    <Mic className="w-4 h-4" />
                  </div>
                  <div>
                    <strong className="block text-xs font-bold text-white">2. Microphone</strong>
                    <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                      HD audio/video introductions with confirmed companions.
                    </p>
                  </div>
                </div>

                <div className="p-3.5 rounded-2xl bg-slate-950/70 border border-slate-800 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0 mt-0.5">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div>
                    <strong className="block text-xs font-bold text-white">3. Location</strong>
                    <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                      Nearby companion discovery & 1-click meeting routes.
                    </p>
                  </div>
                </div>

                <div className="p-3.5 rounded-2xl bg-slate-950/70 border border-slate-800 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-400 shrink-0 mt-0.5">
                    <Bell className="w-4 h-4" />
                  </div>
                  <div>
                    <strong className="block text-xs font-bold text-white">4. Notifications</strong>
                    <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                      Booking acceptance, companion chats, & completion OTPs.
                    </p>
                  </div>
                </div>
              </div>

              {/* Privacy Notice */}
              <div className="p-3.5 rounded-2xl bg-purple-950/30 border border-purple-800/40 flex items-center gap-2.5 text-xs text-purple-200">
                <Lock className="w-4 h-4 text-purple-400 shrink-0" />
                <span>
                  Your privacy is paramount. Permissions are only accessed during active use and can be revoked anytime in Settings.
                </span>
              </div>

              {/* Continue Action Button */}
              <button
                type="button"
                onClick={() => setCurrentStep('camera')}
                className="w-full py-3 px-4 rounded-2xl bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 hover:from-pink-500 hover:to-indigo-500 text-white font-bold text-sm shadow-lg shadow-purple-950 flex items-center justify-center gap-2 transition-all cursor-pointer active:scale-98"
              >
                <span>Continue to Permissions</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* ========================================================= */}
          {/* 2. CAMERA PERMISSION SCREEN */}
          {/* ========================================================= */}
          {currentStep === 'camera' && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="flex items-center justify-between text-xs text-slate-400 font-mono">
                <span>PERMISSION 1 OF 4</span>
                <span className="text-pink-400 font-bold">CAMERA</span>
              </div>

              {/* Mock System Permission Box */}
              <div className="p-5 rounded-3xl bg-slate-950 border border-pink-500/30 space-y-4 text-center">
                <div className="w-14 h-14 rounded-2xl bg-pink-500/20 border border-pink-500/40 text-pink-400 flex items-center justify-center mx-auto shadow-md">
                  <Camera className="w-7 h-7" />
                </div>
                <div className="space-y-1">
                  <span className="text-[11px] font-mono text-slate-400">PairUpToday System Dialog</span>
                  <h3 className="text-base sm:text-lg font-black text-white">
                    “Allow PairUpToday to take pictures and record video?”
                  </h3>
                  <p className="text-xs text-slate-400 max-w-sm mx-auto">
                    Used for capturing your live selfie during instant KYC identity verification and profile photo capture.
                  </p>
                </div>
              </div>

              {/* Deny / Error Warning if Denied */}
              {permissions.camera.state === 'denied' && (
                <div className="p-4 rounded-2xl bg-rose-950/40 border border-rose-800/60 space-y-2">
                  <div className="flex items-center gap-2 text-rose-300 font-bold text-xs">
                    <XCircle className="w-4 h-4 text-rose-400 shrink-0" />
                    <span>Camera Permission Denied</span>
                  </div>
                  <p className="text-xs text-rose-200/90 leading-relaxed">
                    📷 <strong>Impact:</strong> Live camera selfie KYC verification will be disabled. You will need to upload pre-existing photo files manually.
                  </p>
                </div>
              )}

              {/* Why We Need It Accordion */}
              <div className="border border-slate-800 rounded-2xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowWhyDetails(!showWhyDetails)}
                  className="w-full p-3 bg-slate-950/60 flex items-center justify-between text-xs font-bold text-slate-300 hover:text-white"
                >
                  <div className="flex items-center gap-2">
                    <Info className="w-3.5 h-3.5 text-pink-400" />
                    <span>Why we need Camera permission</span>
                  </div>
                  {showWhyDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                {showWhyDetails && (
                  <div className="p-3.5 bg-slate-950 text-xs text-slate-400 space-y-1.5 border-t border-slate-800 leading-relaxed">
                    <p>• Ensures 100% genuine, real hosts and clients to prevent catfish and fraudulent profiles.</p>
                    <p>• Camera stream is only active during the verification capture and automatically closes afterwards.</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={handleRequestCamera}
                  disabled={isRequesting}
                  className="w-full py-3 px-4 rounded-2xl bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white font-bold text-sm shadow-lg flex items-center justify-center gap-2 transition-all cursor-pointer active:scale-98"
                >
                  {isRequesting ? (
                    <RotateCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                  <span>Allow Camera Access</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowWhyDetails(false);
                    setCurrentStep('microphone');
                  }}
                  className="w-full py-2.5 px-4 rounded-2xl text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 text-xs font-semibold transition-all cursor-pointer"
                >
                  {permissions.camera.state === 'denied' ? 'Continue with camera disabled →' : 'Not Now / Skip Camera'}
                </button>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* 3. MICROPHONE PERMISSION SCREEN */}
          {/* ========================================================= */}
          {currentStep === 'microphone' && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="flex items-center justify-between text-xs text-slate-400 font-mono">
                <span>PERMISSION 2 OF 4</span>
                <span className="text-purple-400 font-bold">MICROPHONE</span>
              </div>

              {/* Mock System Permission Box */}
              <div className="p-5 rounded-3xl bg-slate-950 border border-purple-500/30 space-y-4 text-center">
                <div className="w-14 h-14 rounded-2xl bg-purple-500/20 border border-purple-500/40 text-purple-400 flex items-center justify-center mx-auto shadow-md">
                  <Mic className="w-7 h-7" />
                </div>
                <div className="space-y-1">
                  <span className="text-[11px] font-mono text-slate-400">PairUpToday System Dialog</span>
                  <h3 className="text-base sm:text-lg font-black text-white">
                    “Allow PairUpToday to record audio?”
                  </h3>
                  <p className="text-xs text-slate-400 max-w-sm mx-auto">
                    Used for in-app voice notes and audio/video calls with confirmed companions before meetups.
                  </p>
                </div>
              </div>

              {/* Deny Warning */}
              {permissions.microphone.state === 'denied' && (
                <div className="p-4 rounded-2xl bg-rose-950/40 border border-rose-800/60 space-y-2">
                  <div className="flex items-center gap-2 text-rose-300 font-bold text-xs">
                    <XCircle className="w-4 h-4 text-rose-400 shrink-0" />
                    <span>Microphone Permission Denied</span>
                  </div>
                  <p className="text-xs text-rose-200/90 leading-relaxed">
                    🎤 <strong>Impact:</strong> Voice and video call audio will be disabled. You can still communicate via encrypted private text chat.
                  </p>
                </div>
              )}

              {/* Why We Need It Accordion */}
              <div className="border border-slate-800 rounded-2xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowWhyDetails(!showWhyDetails)}
                  className="w-full p-3 bg-slate-950/60 flex items-center justify-between text-xs font-bold text-slate-300 hover:text-white"
                >
                  <div className="flex items-center gap-2">
                    <Info className="w-3.5 h-3.5 text-purple-400" />
                    <span>Why we need Microphone permission</span>
                  </div>
                  {showWhyDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                {showWhyDetails && (
                  <div className="p-3.5 bg-slate-950 text-xs text-slate-400 space-y-1.5 border-t border-slate-800 leading-relaxed">
                    <p>• Enables quick audio calls between confirmed client and companion to coordinate meeting logistics.</p>
                    <p>• No background listening or persistent audio recording occurs.</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={handleRequestMicrophone}
                  disabled={isRequesting}
                  className="w-full py-3 px-4 rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-sm shadow-lg flex items-center justify-center gap-2 transition-all cursor-pointer active:scale-98"
                >
                  {isRequesting ? (
                    <RotateCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mic className="w-4 h-4" />
                  )}
                  <span>Allow Microphone Access</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowWhyDetails(false);
                    setCurrentStep('location');
                  }}
                  className="w-full py-2.5 px-4 rounded-2xl text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 text-xs font-semibold transition-all cursor-pointer"
                >
                  {permissions.microphone.state === 'denied' ? 'Continue with microphone disabled →' : 'Not Now / Skip Microphone'}
                </button>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* 4. LOCATION PERMISSION SCREEN */}
          {/* ========================================================= */}
          {currentStep === 'location' && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="flex items-center justify-between text-xs text-slate-400 font-mono">
                <span>PERMISSION 3 OF 4</span>
                <span className="text-emerald-400 font-bold">LOCATION</span>
              </div>

              {/* Mock System Permission Box */}
              <div className="p-5 rounded-3xl bg-slate-950 border border-emerald-500/30 space-y-4 text-center">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center mx-auto shadow-md">
                  <MapPin className="w-7 h-7" />
                </div>
                <div className="space-y-1">
                  <span className="text-[11px] font-mono text-slate-400">PairUpToday System Dialog</span>
                  <h3 className="text-base sm:text-lg font-black text-white">
                    “Allow PairUpToday to access your location?”
                  </h3>
                  <p className="text-xs text-slate-400 max-w-sm mx-auto">
                    Select <strong>“Allow While Using App”</strong> to discover companions in your neighborhood and get accurate travel directions on meeting day.
                  </p>
                </div>
              </div>

              {/* Deny Warning */}
              {permissions.location.state === 'denied' && (
                <div className="p-4 rounded-2xl bg-rose-950/40 border border-rose-800/60 space-y-2">
                  <div className="flex items-center gap-2 text-rose-300 font-bold text-xs">
                    <XCircle className="w-4 h-4 text-rose-400 shrink-0" />
                    <span>Location Permission Denied</span>
                  </div>
                  <p className="text-xs text-rose-200/90 leading-relaxed">
                    📍 <strong>Impact:</strong> Location-based companion discovery will be disabled. You will manually choose your city (Kolkata, Delhi, Mumbai, etc.) from the search selector.
                  </p>
                </div>
              )}

              {/* Why We Need It Accordion */}
              <div className="border border-slate-800 rounded-2xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowWhyDetails(!showWhyDetails)}
                  className="w-full p-3 bg-slate-950/60 flex items-center justify-between text-xs font-bold text-slate-300 hover:text-white"
                >
                  <div className="flex items-center gap-2">
                    <Info className="w-3.5 h-3.5 text-emerald-400" />
                    <span>Why we need Location permission</span>
                  </div>
                  {showWhyDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                {showWhyDetails && (
                  <div className="p-3.5 bg-slate-950 text-xs text-slate-400 space-y-1.5 border-t border-slate-800 leading-relaxed">
                    <p>• Ranks nearby verified companions first so you can book meets with minimal travel delays.</p>
                    <p>• Only checks foreground location while using the app; no background tracking.</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={handleRequestLocation}
                  disabled={isRequesting}
                  className="w-full py-3 px-4 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-sm shadow-lg flex items-center justify-center gap-2 transition-all cursor-pointer active:scale-98"
                >
                  {isRequesting ? (
                    <RotateCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <MapPin className="w-4 h-4" />
                  )}
                  <span>Allow While Using App</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowWhyDetails(false);
                    setCurrentStep('notification');
                  }}
                  className="w-full py-2.5 px-4 rounded-2xl text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 text-xs font-semibold transition-all cursor-pointer"
                >
                  {permissions.location.state === 'denied' ? 'Continue with manual city selection →' : 'Not Now / Skip Location'}
                </button>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* 5. NOTIFICATION PERMISSION SCREEN */}
          {/* ========================================================= */}
          {currentStep === 'notification' && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="flex items-center justify-between text-xs text-slate-400 font-mono">
                <span>PERMISSION 4 OF 4</span>
                <span className="text-blue-400 font-bold">NOTIFICATIONS</span>
              </div>

              {/* Mock System Permission Box */}
              <div className="p-5 rounded-3xl bg-slate-950 border border-blue-500/30 space-y-4 text-center">
                <div className="w-14 h-14 rounded-2xl bg-blue-500/20 border border-blue-500/40 text-blue-400 flex items-center justify-center mx-auto shadow-md">
                  <Bell className="w-7 h-7" />
                </div>
                <div className="space-y-1">
                  <span className="text-[11px] font-mono text-slate-400">PairUpToday System Dialog</span>
                  <h3 className="text-base sm:text-lg font-black text-white">
                    “Allow PairUpToday to send you notifications?”
                  </h3>
                  <p className="text-xs text-slate-400 max-w-sm mx-auto">
                    Stay updated when a companion accepts your booking, a chat message arrives, or your 6-digit meeting completion OTP is generated.
                  </p>
                </div>
              </div>

              {/* Deny Warning */}
              {permissions.notification.state === 'denied' && (
                <div className="p-4 rounded-2xl bg-rose-950/40 border border-rose-800/60 space-y-2">
                  <div className="flex items-center gap-2 text-rose-300 font-bold text-xs">
                    <XCircle className="w-4 h-4 text-rose-400 shrink-0" />
                    <span>Notification Permission Denied</span>
                  </div>
                  <p className="text-xs text-rose-200/90 leading-relaxed">
                    🔔 <strong>Impact:</strong> Push notifications disabled. You will need to keep the app open to receive new booking alerts and OTP codes.
                  </p>
                </div>
              )}

              {/* Why We Need It Accordion */}
              <div className="border border-slate-800 rounded-2xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowWhyDetails(!showWhyDetails)}
                  className="w-full p-3 bg-slate-950/60 flex items-center justify-between text-xs font-bold text-slate-300 hover:text-white"
                >
                  <div className="flex items-center gap-2">
                    <Info className="w-3.5 h-3.5 text-blue-400" />
                    <span>Why we need Notification permission</span>
                  </div>
                  {showWhyDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                {showWhyDetails && (
                  <div className="p-3.5 bg-slate-950 text-xs text-slate-400 space-y-1.5 border-t border-slate-800 leading-relaxed">
                    <p>• In-time notification when a companion accepts your request so you can open private chat.</p>
                    <p>• Delivery of the secure 6-digit meetup completion OTP on the day of meeting.</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={handleRequestNotification}
                  disabled={isRequesting}
                  className="w-full py-3 px-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-sm shadow-lg flex items-center justify-center gap-2 transition-all cursor-pointer active:scale-98"
                >
                  {isRequesting ? (
                    <RotateCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Bell className="w-4 h-4" />
                  )}
                  <span>Allow Notifications</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowWhyDetails(false);
                    setCurrentStep('summary');
                  }}
                  className="w-full py-2.5 px-4 rounded-2xl text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 text-xs font-semibold transition-all cursor-pointer"
                >
                  {permissions.notification.state === 'denied' ? 'Continue with push alerts disabled →' : 'Not Now / Skip Notifications'}
                </button>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* 6. SUMMARY / COMPLETION SCREEN */}
          {/* ========================================================= */}
          {currentStep === 'summary' && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="text-center space-y-1.5">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center mx-auto mb-2">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-black text-white">All Permissions Handled!</h3>
                <p className="text-xs text-slate-300">
                  Your device permission preferences have been saved. You can modify these anytime in <strong>Settings</strong>.
                </p>
              </div>

              {/* Status Checklist Table */}
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2.5">
                <div className="flex items-center justify-between text-xs pb-2 border-b border-slate-800">
                  <div className="flex items-center gap-2 text-slate-300">
                    <Camera className="w-4 h-4 text-pink-400" />
                    <span className="font-bold">Camera</span>
                  </div>
                  {permissions.camera.state === 'granted' ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded-md border border-emerald-800">
                      <CheckCircle2 className="w-3 h-3" /> Granted
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-400 bg-amber-950/80 px-2 py-0.5 rounded-md border border-amber-800">
                      <AlertCircle className="w-3 h-3" /> Skipped / Denied
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between text-xs pb-2 border-b border-slate-800">
                  <div className="flex items-center gap-2 text-slate-300">
                    <Mic className="w-4 h-4 text-purple-400" />
                    <span className="font-bold">Microphone</span>
                  </div>
                  {permissions.microphone.state === 'granted' ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded-md border border-emerald-800">
                      <CheckCircle2 className="w-3 h-3" /> Granted
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-400 bg-amber-950/80 px-2 py-0.5 rounded-md border border-amber-800">
                      <AlertCircle className="w-3 h-3" /> Skipped / Denied
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between text-xs pb-2 border-b border-slate-800">
                  <div className="flex items-center gap-2 text-slate-300">
                    <MapPin className="w-4 h-4 text-emerald-400" />
                    <span className="font-bold">Location</span>
                  </div>
                  {permissions.location.state === 'granted' ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded-md border border-emerald-800">
                      <CheckCircle2 className="w-3 h-3" /> Granted
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-400 bg-amber-950/80 px-2 py-0.5 rounded-md border border-amber-800">
                      <AlertCircle className="w-3 h-3" /> Skipped / Denied
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 text-slate-300">
                    <Bell className="w-4 h-4 text-blue-400" />
                    <span className="font-bold">Notifications</span>
                  </div>
                  {permissions.notification.state === 'granted' ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded-md border border-emerald-800">
                      <CheckCircle2 className="w-3 h-3" /> Granted
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-400 bg-amber-950/80 px-2 py-0.5 rounded-md border border-amber-800">
                      <AlertCircle className="w-3 h-3" /> Skipped / Denied
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => handleFinishOnboarding(false)}
                  className="w-full py-3.5 px-4 rounded-2xl bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 hover:from-pink-500 hover:to-indigo-500 text-white font-bold text-sm shadow-xl flex items-center justify-center gap-2 transition-all cursor-pointer active:scale-98"
                >
                  <span>Continue to PairUpToday Home</span>
                  <ArrowRight className="w-4 h-4" />
                </button>

                <button
                  type="button"
                  onClick={() => handleFinishOnboarding(true)}
                  className="w-full py-2.5 px-4 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-bold transition-all cursor-pointer"
                >
                  Proceed Directly to Sign In / Sign Up
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
